import React, { useEffect, useState, useRef, useCallback } from "react";
import SuitabilityBar from "../../../components/suitabilityBar";
import { connect } from "react-redux";
import productService from "../../../services/productService";
import { IProduct } from "../../../interfaces/products";
import { Table, Button, FormGroup, InputGroup, Input } from "reactstrap";
import {
	ArrowRight,
	Box,
	ShoppingCart,
	Sliders,
	ChevronDown,
	Bell,
	Loader as LoaderIcon,
	Search,
	X
} from "react-feather";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { IApplicationState } from "../../../redux/reducers";
import Loader from "../../../components/loader";
import { IPaginateList } from "../../../interfaces/pagination";
import { routes } from "../../routes";
import Pagination from "../../../components/pagination";
import { paginateAction } from "../../../redux/actions/pagination/pagination";
import { Dispatch } from "redux";
import { useDispatch } from "react-redux";
import { product } from "../../../redux/actions/actionContants";
import { setProductFiltersAction, clearProductFiltersAction } from "../../../redux/actions/products/products";
import { IUser } from "../../../interfaces/user";
import Upgrade from "../../../components/common/upgrade";
import ProgressGradient from "../../../components/common/progressGradient";
import { IBrand } from "../../../interfaces/company";
import ReactImageFallback from "react-image-fallback";
import { getProductImagePlaceholder } from "../../../utils/ingredient";
import { toCamelCase } from "../../../utils/string";
import { TableSkeleton } from "../../../utils/ghostUI";

interface IProps extends RouteComponentProps {
	active: IPaginateList<IProduct[]> & { cache: { [page: number]: IProduct[] } };
	concept: IPaginateList<IProduct[]> & { cache: { [page: number]: IProduct[] } };
	viewType: String;
	getActiveProducts: () => Promise<any>;
	getConceptProducts: () => Promise<any>;
	getAllProductsForFilters: () => Promise<IProduct[]>;
	getAllConceptProductsForFilters: () => Promise<IProduct[]>;
	allProducts?: boolean;
	paginateConceptProducts: (page: number) => void;
	paginateActiveProducts: (page: number) => void;
	profile?: IUser | undefined;
	selected_brand: IBrand;
	filters: {
		selectedMarket: string;
		selectedBrand: string;
		selectedType: string;
		searchTerm: string;
	};
	setProductFilters: (filters: {
		selectedMarket?: string;
		selectedBrand?: string;
		selectedType?: string;
		searchTerm?: string;
	}) => void;
	clearProductFilters: () => void;
}

const ProductList: React.FC<IProps> = React.memo(({
	concept,
	paginateConceptProducts,
	paginateActiveProducts,
	filters,
	setProductFilters,
	clearProductFilters,
	...props
}) => {
	const { list, pagination } = concept;
	const [loading, setLoading] = useState<boolean>(false);
	const [paginationLoading, setPaginationLoading] = useState<boolean>(false);
	const [productViewType, setProductViewType] = useState<String>(props.viewType || "active");
	// Keep a reference of all brand, market, and types for retail or concept products
	const [brands, setBrands] = useState<(string | undefined)[]>([]);
	const [markets, setMarkets] = useState<(string | undefined)[]>([]);
	const [types, setTypes] = useState<(string | undefined)[]>([]);
	const [show, setShow] = useState<boolean>(false);
	const [searchProducts, setSearchProducts] = useState<IProduct[]>([]);

	// Add state for client-side pagination (for Journey AI and Latest Updates)
	const [clientSidePage, setClientSidePage] = useState<number>(1);
	const [clientSidePageSize] = useState<number>(10);

	// Use global filter state
	const { selectedMarket, selectedBrand, selectedType, searchTerm } = filters;

	// AbortController ref for canceling requests
	const abortControllerRef = useRef<AbortController | null>(null);

	// Track the latest request ID to ignore stale responses
	const requestIdRef = useRef<number>(0);

	// Track last pagination click time to prevent rapid clicks
	const lastPaginationClickRef = useRef<number>(0);

	const dispatch = useDispatch<Dispatch<any>>();

	useEffect(() => {
		// Cancel any pending requests
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		// Create new AbortController for this request
		const abortController = new AbortController();
		abortControllerRef.current = abortController;
		const signal = abortController.signal;

		// Generate unique request ID
		const currentRequestId = ++requestIdRef.current;

		const fetchProducts = async () => {
			try {
				setLoading(true);
				setPaginationLoading(true);

				// Pass is_ai_generated=true when Journey AI filter is active
				const isAIGenerated = productViewType === "journey";

				const currentActivePage = props.active?.pagination?.page || 1;
				const currentConceptPage = concept?.pagination?.page || 1;

				const skipActive = props.active?.cache && props.active.cache[currentActivePage];
				const skipConcept = concept?.cache && concept.cache[currentConceptPage];

				if (skipActive && skipConcept) {
					setLoading(false);
					setPaginationLoading(false);
					return;
				}

				// Call service methods with signal through dispatch
				const activeProductsPromise = skipActive
					? Promise.resolve()
					: dispatch(productService.getActiveProducts(signal, isAIGenerated) as any);

				const conceptProductsPromise = skipConcept
					? Promise.resolve()
					: dispatch(productService.getConceptProducts(signal, isAIGenerated) as any);

				await Promise.all([activeProductsPromise, conceptProductsPromise]);
			} catch (error) {
				// Don't set loading to false if request was aborted
				const err = error as any;
				if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
					console.error("Error fetching products:", error);
				}
			} finally {
				// Only update loading state if this is still the latest request and not aborted
				if (currentRequestId === requestIdRef.current && !signal.aborted) {
					setLoading(false);
					setPaginationLoading(false);
				}
			}
		};

		fetchProducts();

		// Cleanup: abort request when component unmounts or dependencies change
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
		};
	}, [pagination?.page, props.selected_brand, props.active?.pagination?.page, dispatch, productViewType]);

	// AbortController ref for filter options
	const filterAbortControllerRef = useRef<AbortController | null>(null);

	// Update filter options from ALL products (not just current page)
	useEffect(() => {
		// Cancel any pending filter requests
		if (filterAbortControllerRef.current) {
			filterAbortControllerRef.current.abort();
		}

		// Create new AbortController for filter request
		const abortController = new AbortController();
		filterAbortControllerRef.current = abortController;
		const signal = abortController.signal;

		const loadFilterOptions = async () => {
			// Add a small delay to avoid competing with initial product fetch
			await new Promise(resolve => setTimeout(resolve, 800));
			if (signal.aborted) return;

			try {
				let allProducts: IProduct[] = [];

				// Get all products for filter options based on view type
				// These thunk actions return the data directly when called
				if (productViewType === "concept") {
					const thunk = productService.getAllConceptProductsForFilters(signal);
					allProducts = await thunk(dispatch, () => ({} as any)) as IProduct[];
				} else {
					const thunk = productService.getAllProductsForFilters(signal);
					allProducts = await thunk(dispatch, () => ({} as any)) as IProduct[];
				}

				// Only process if request wasn't aborted
				if (!signal.aborted) {
					// If API call failed or returned no products, fall back to current products
					if (!allProducts || allProducts.length === 0) {
						if (productViewType === "concept") {
							allProducts = list || [];
						} else {
							allProducts = props.active?.list || [];
						}
					}

					if (allProducts && allProducts.length > 0) {
						getProductBrands(allProducts);
						getProductMarkets(allProducts);
						getProductTypes(allProducts);
					}
				}
			} catch (error) {
				// Don't process error if request was aborted
				const err = error as any;
				if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
					console.error("Error loading filter options:", error);
					// Fallback to current products on error
					const fallbackProducts = productViewType === "concept" ? (list || []) : (props.active?.list || []);
					if (fallbackProducts.length > 0) {
						getProductBrands(fallbackProducts);
						getProductMarkets(fallbackProducts);
						getProductTypes(fallbackProducts);
					}
				}
			}
		};

		loadFilterOptions();

		// Cleanup: abort request when component unmounts or view type changes
		return () => {
			if (filterAbortControllerRef.current) {
				filterAbortControllerRef.current.abort();
				filterAbortControllerRef.current = null;
			}
		};
	}, [productViewType, dispatch, list, props.active?.list]); // Only reload when view type changes, not on every page change

	// Note: Filters are now global and persistent across view type changes

	// const getProductBrands = (products: IProduct[]) => {
	// 	let all = products
	// 		.filter((p) => p.brand?.name != null || p.brand?.name != undefined)
	// 		.map((p) => p.brand?.name);
	// 	let uniq = all.filter((value, index, array) => {
	// 		return array.indexOf(value) == index;
	// 	});
	// 	setBrands(uniq);
	// };
	const getProductBrands = (products: IProduct[]) => {
		const all = products
			.filter((p) => p.brand?.name != null && p.brand?.name != undefined) // <-- && not ||
			.map((p) => p.brand?.name);
		const uniq = all.filter((v, i, a) => a.indexOf(v) === i);
		setBrands(uniq);
	};

	const getProductMarkets = (products: IProduct[]) => {
		let all = products
			.filter((p) => p.country?.alpha_3 != null && p.country?.alpha_3 != undefined)
			.map((p) => p.country?.alpha_3);
		let uniq = all.filter((value, index, array) => {
			return array.indexOf(value) == index;
		});
		setMarkets(uniq);
	};

	const getProductTypes = (products: IProduct[]) => {
		let all = products
			.filter((p) => p.product_type?.title != null && p.product_type?.title != undefined)
			.map((p) => p.product_type?.title);
		let uniq = all.filter((value, index, array) => {
			return array.indexOf(value) == index;
		});
		setTypes(uniq);
	};

	// const filterProducts = () => {
	// 	let products = props.allProducts
	// 		? productViewType == "active"
	// 			? props.active.list
	// 			: list
	// 		: props.viewType == "active"
	// 			? props.active.list
	// 			: list;
	// 	products = filterMostRecent(products);

	// 	return products;
	// };

	// ✅ Replace filterProducts with a version that also applies text search
	const filterProducts = () => {
		let products = [];

		// Handle different view types
		if (props.allProducts) {
			switch (productViewType) {
				case "active":
					products = props.active.list || [];
					break;
				case "concept":
					products = list || [];
					break;
				case "updated":
					// For "Latest updates", show both active and concept products
					products = [...(props.active.list || []), ...(list || [])];
					break;
				case "journey":
					// For "Journey AI", show products with recommendations OR products generated by Journey AI
					// Backend filtering is handled via is_ai_generated query parameter
					products = [...(props.active.list || []), ...(list || [])].filter(p => {
						// Check for recommendations (existing behavior)
						const hasRecommendations = p.has_recommendations || p?.recommendations_count > 0;
						// Check for AI-generated products (new behavior)
						const isAIGenerated = p?.is_ai_generated === true;
						const shouldInclude = hasRecommendations || isAIGenerated;

						// Debug logging to verify filter is working
						if (isAIGenerated) {
							console.log("Journey AI filter: Found AI-generated product:", p.name, "is_ai_generated:", p.is_ai_generated);
						}

						return shouldInclude;
					});
					break;
				default:
					products = props.active.list || [];
			}
		} else {
			products = props.viewType == "active" ? props.active.list : list;
		}

		products = filterMostRecent(products);

		// Apply filters
		if (selectedMarket) {
			products = products.filter((p) => p.country?.alpha_3 === selectedMarket);
		}

		if (selectedBrand) {
			products = products.filter((p) => p.brand?.name === selectedBrand);
		}

		if (selectedType) {
			products = products.filter((p) => p.product_type?.title === selectedType);
		}

		// Apply text search
		const q = searchTerm.trim().toLowerCase();
		if (q) {
			products = products.filter((p) => {
				const name = p.name ?? "";
				const brand = p.brand?.name ?? "";
				const type = p.product_type?.title ?? "";
				const flavor = p.flavor ?? "";
				return (
					name.toLowerCase().includes(q) ||
					brand.toLowerCase().includes(q) ||
					type.toLowerCase().includes(q) ||
					flavor.toLowerCase().includes(q)
				);
			});
		}

		return products;
	};

	// Get paginated products for client-side pagination (Journey AI and Latest Updates)
	const getPaginatedProducts = () => {
		const allFilteredProducts = filterProducts();
		const startIndex = (clientSidePage - 1) * clientSidePageSize;
		const endIndex = startIndex + clientSidePageSize;
		return allFilteredProducts.slice(startIndex, endIndex);
	};

	// Get products to display based on view type
	const getDisplayProducts = () => {
		if (productViewType === "updated" || productViewType === "journey") {
			return getPaginatedProducts();
		}
		return filterProducts();
	};

	const filterMostRecent = (products: IProduct[]) => {
		let mostRecent: IProduct[] = [];
		products?.forEach((product: IProduct) => {
			const currProduct = mostRecent.find((p: IProduct) => p.name == product.name);
			const currIndex = currProduct ? mostRecent.indexOf(currProduct) : -1;
			currProduct && currIndex > -1 && product.version > currProduct.version
				? (mostRecent[currIndex] = product)
				: mostRecent.push(product);
		});
		return mostRecent;
	};

	async function handleSearchChange(e: any) {
		const search = e.target.value;
		setProductFilters({ searchTerm: search });
		try {
			// const data = await props.searchIngredient(search);
			// setSearchProducts(data);
		} catch { }
	}

	const clearSearch = () => {
		setSearchProducts([]);
		setProductFilters({ searchTerm: "" });
	};

	// Filter handlers
	const handleMarketChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setProductFilters({ selectedMarket: e.target.value });
	};

	const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setProductFilters({ selectedBrand: e.target.value });
	};

	const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setProductFilters({ selectedType: e.target.value });
	};

	// Clear all filters
	const clearAllFilters = () => {
		clearProductFilters();
	};

	// Handle client-side pagination for Journey AI and Latest Updates
	const handleClientSidePageChange = useCallback((page: number) => {
		// Prevent pagination changes while loading
		if (loading || paginationLoading) {
			return;
		}
		
		// Debounce pagination clicks (300ms minimum between clicks)
		const now = Date.now();
		if (now - lastPaginationClickRef.current < 300) {
			return;
		}
		lastPaginationClickRef.current = now;
		
		setClientSidePage(page);
	}, [loading, paginationLoading]);
	
	// Wrapped pagination handlers with race condition protection
	const handleConceptPaginationChange = useCallback((page: number) => {
		// Prevent pagination changes while loading
		if (loading || paginationLoading) {
			return;
		}

		// Debounce pagination clicks (300ms minimum between clicks)
		const now = Date.now();
		if (now - lastPaginationClickRef.current < 300) {
			return;
		}
		lastPaginationClickRef.current = now;

		setPaginationLoading(true);
		paginateConceptProducts(page);
	}, [loading, paginationLoading, paginateConceptProducts]);

	const handleActivePaginationChange = useCallback((page: number) => {
		// Prevent pagination changes while loading
		if (loading || paginationLoading) {
			return;
		}

		// Debounce pagination clicks (300ms minimum between clicks)
		const now = Date.now();
		if (now - lastPaginationClickRef.current < 300) {
			return;
		}
		lastPaginationClickRef.current = now;

		setPaginationLoading(true);
		paginateActiveProducts(page);
	}, [loading, paginationLoading, paginateActiveProducts]);

	// Reset client-side pagination when view type or filters change
	useEffect(() => {
		setClientSidePage(1);
	}, [productViewType, selectedMarket, selectedBrand, selectedType, searchTerm]);

	return (
		<div className="dash-list">
			{props.allProducts ? (
				<div style={{ paddingTop: 45 }}>
					{/* Header Section with Navigation Buttons, Title, Count, and View Toggles */}
					<div className="header-section">
						{/* Left Side - Title and Count First, Then Navigation Buttons */}
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "16px",
								flexWrap: "wrap"
							}}
						>
							{/* Title and Count First */}
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "8px"
								}}
							>
								<h2 style={{
									fontWeight: 600,
									fontSize: "1.5rem",
									margin: 0
								}}>
									{productViewType == "active"
										? "Active Products"
										: productViewType == "concept"
											? "Concept Products"
											: productViewType == "updated"
												? "Latest Updates"
												: productViewType == "journey"
													? "Journey AI Products"
													: "Active Products"}
								</h2>
								<span
									style={{
										color: "#3C7CD1",
										fontWeight: 600,
										fontSize: "1.5rem",
										marginLeft: "4px"
									}}
								>
									{props.profile?.company?.subscription_type == "supplier" ? (
										"-"
									) : (
										<>
											{loading ? (
												<LoaderIcon className={"fa-spin"} />
											) : productViewType == "active" ? (
												props.active?.pagination?.total
											) : productViewType == "concept" ? (
												pagination?.total
											) : productViewType == "updated" ? (
												(props.active?.pagination?.total || 0) + (pagination?.total || 0)
											) : productViewType == "journey" ? (
												filterProducts().length
											) : (
												props.active?.pagination?.total
											)}
										</>
									)}
								</span>
							</div>

							{/* Navigation Buttons After Title */}
							<div className="navigation-buttons">
								<Button
									onClick={() => setProductViewType("active")}
									className={`btn-round filter-btn-retail ${productViewType === "active" ? "active" : "inactive"}`}
								>
									<ShoppingCart size={16} />
									Retail
								</Button>
								<Button
									onClick={() => setProductViewType("concept")}
									className={`btn-round filter-btn-concept ${productViewType === "concept" ? "active" : "inactive"}`}
								>
									<Box size={16} />
									Concept
								</Button>
								<Button
									onClick={() => setProductViewType("updated")}
									className={`btn-round filter-btn-updates ${productViewType === "updated" ? "active" : "inactive"}`}
								>
									<Bell size={16} />
									Latest updates
								</Button>
								<Button
									onClick={() => setProductViewType("journey")}
									className={`btn-round filter-btn-journey ${productViewType === "journey" ? "active" : "inactive"}`}
								>
									<img
										className="journey-icon"
										src={require("../../../assets/images/JourneyAI_white.png")}
										alt="Journey AI"
										style={{ width: "16px", height: "16px" }}
									/>
									Journey AI
								</Button>
							</div>
						</div>

					</div>

					{/* Filters Section */}
					<div className="filters-section">
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "8px",
								color: "#657280"
							}}
						>
							<Sliders size={20} />
							<span style={{ fontWeight: 500 }}>Filters:</span>
						</div>

						{/* Market Filter */}
						<div className="filter-dropdown">
							<select
								value={selectedMarket}
								onChange={handleMarketChange}
							>
								<option value="">Market</option>
								{markets.map((market, index) => (
									<option key={market || `market-${index}`} value={market}>{market}</option>
								))}
							</select>
						</div>

						{/* Brand Filter */}
						<div className="filter-dropdown">
							<select
								value={selectedBrand}
								onChange={handleBrandChange}
							>
								<option value="">Brand</option>
								{brands.map((brand, index) => (
									<option key={brand || `brand-${index}`} value={brand}>{brand}</option>
								))}
							</select>
						</div>

						{/* Type Filter */}
						<div className="filter-dropdown">
							<select
								value={selectedType}
								onChange={handleTypeChange}
							>
								<option value="">Type</option>
								{types.map((type, index) => (
									<option key={type || `type-${index}`} value={type}>{type}</option>
								))}
							</select>
						</div>

						{/* Clear Filters Button */}
						{(selectedMarket || selectedBrand || selectedType) && (
							<button
								onClick={clearAllFilters}
								style={{
									background: "#F3F4F6",
									border: "1px solid #E5E7EB",
									borderRadius: "6px",
									padding: "8px 12px",
									color: "#6B7280",
									fontSize: "14px",
									cursor: "pointer",
									display: "flex",
									alignItems: "center",
									gap: "4px"
								}}
							>
								<X size={16} />
								Clear
							</button>
						)}
					</div>


					{/* Pagination - Right Aligned */}
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							marginBottom: "36px"
						}}
					>
						{productViewType == "concept" ? (
							<Pagination
								className="pagination-right pagination-small"
								pages={pagination?.pages}
								page={pagination?.page}
								size={pagination?.size}
								total={pagination?.total}
								loading={loading || paginationLoading}
								handlePageChange={handleConceptPaginationChange}
							/>
						) : productViewType == "updated" || productViewType == "journey" ? (
							<Pagination
								className="pagination-right pagination-small"
								pages={Math.ceil(filterProducts().length / clientSidePageSize)}
								page={clientSidePage}
								size={clientSidePageSize}
								total={filterProducts().length}
								loading={loading || paginationLoading}
								handlePageChange={handleClientSidePageChange}
							/>
						) : (
							<Pagination
								className="pagination-right pagination-small"
								pages={props.active?.pagination?.pages}
								page={props.active?.pagination?.page}
								size={props.active?.pagination?.size}
								total={props.active?.pagination?.total}
								loading={loading || paginationLoading}
								handlePageChange={handleActivePaginationChange}
							/>
						)}
					</div>
					{/* <span className={"filters"}>
						<span className={"filter-title"}>
							<Sliders color={"#657280"} size={24} /> Filters:{" "}
						</span>
						<div
							className="filter"
							onMouseOver={() => toggleFilterMenu("market")}
							onMouseLeave={() => toggleFilterMenu(undefined)}
						>
							<span className={marketFilters.length > 0 ? "filter-menu active" : "filter-menu"}>
								Market
								<ChevronDown size={16} />
							</span>
							{filterMenu == "market" ? (
								<ul className="list-filters">
									{markets.length > 0 ? (
										markets.map((market) => {
											return market != undefined ? (
												<li
													className={marketFilters.includes(market) ? "active" : ""}
													onClick={(e) => {
														handleFilterChange(e.currentTarget.innerHTML, "market");
													}}
												>
													{market}
												</li>
											) : (
												<></>
											);
										})
									) : (
										<li>No {productViewType == "active" ? "retail" : "concept"} markets</li>
									)}
								</ul>
							) : (
								""
							)}
						</div>
						<div
							className="filter"
							onMouseOver={() => toggleFilterMenu("brand")}
							onMouseLeave={() => toggleFilterMenu(undefined)}
						>
							<span className={brandFilters.length > 0 ? "filter-menu active" : "filter-menu"}>
								Brand
								<ChevronDown size={16} />
							</span>
							{filterMenu == "brand" ? (
								<ul className="list-filters">
									{brands.length > 0 ? (
										brands.map((brand) => {
											return brand != undefined ? (
												<li
													className={brandFilters.includes(brand) ? "active" : ""}
													onClick={(e) => {
														handleFilterChange(e.currentTarget.innerHTML, "brand");
													}}
												>
													{brand}
												</li>
											) : (
												<></>
											);
										})
									) : (
										<li>No {productViewType == "active" ? "retail" : "concept"} brands</li>
									)}
								</ul>
							) : (
								""
							)}
						</div>
						<div
							className="filter"
							onMouseOver={() => toggleFilterMenu("type")}
							onMouseLeave={() => toggleFilterMenu(undefined)}
						>
							<span className={typeFilters.length > 0 ? "filter-menu active" : "filter-menu"}>
								Type
								<ChevronDown size={16} />
							</span>
							{filterMenu == "type" ? (
								<ul className="list-filters">
									{types.length > 0 ? (
										types.map((type) => {
											return type != undefined ? (
												<li
													className={typeFilters.includes(type) ? "active" : ""}
													onClick={(e) => {
														handleFilterChange(e.currentTarget.innerHTML, "type");
													}}
												>
													{type}
												</li>
											) : (
												<></>
											);
										})
									) : (
										<li>No {productViewType == "active" ? "retail" : "concept"} types</li>
									)}
								</ul>
							) : (
								""
							)}
						</div>
					</span> */}
				</div>
			) : (
				<></>
			)}
			{props.profile?.company.subscription_type == "supplier" ? (
				<div className="mt-4">
					<Upgrade />
				</div>
			) : (
				<div className="table-container">
					<Table borderless className="products-list-table">
						<thead>
							<tr>
								<th className="text-center image-column">Image</th>
								<th className="text-left">Product Name</th>
								<th className="text-center">Brand</th>
								<th className="text-center">Type</th>
								<th className="text-center">Flavor</th>
								<th className="text-center">Version</th>
								<th className="text-center">Country</th>
								<th className="text-center">Status</th>
								<th className="text-center">Score</th>
								<th className="text-center">Actions</th>
							</tr>
						</thead>
						{loading ? (
							<TableSkeleton />
						) : (
							<tbody>
								{/* { (productViewType === 'active' ? props.active : list).map((p, index) => {  */}
								{getDisplayProducts().map((p, index) => {
									// console.log(p);
									return (
										<tr
											className="list-row"
											onClick={() => props.history.push(`${routes.PRODUCTS}/${p.id}`)}
											id={p.active ? "retail" : "concept"}
											key={p.id || `product-${index}`}
										>
											{/* Image Column */}
											<td className="text-center image-column">
												<div className="product-image-container">
													<ReactImageFallback
														className="product-image"
														src={p.image_uri}
														fallbackImage={getProductImagePlaceholder()}
														alt="Product Image"
													/>
												</div>
											</td>

											{/* Product Name Column */}
											<td className="product-name-cell">
												<div className="product-name-content">
													<h6 className="product-name">{p.name}</h6>
													{p?.recommendations_count > 0 && (
														<div className="ai-badge">
															<img
																className="ai-icon"
																src={require("../../../assets/images/JourneyAI_purple.png")}
																alt="AI Recommended"
															/>
															<span>AI Recommended</span>
														</div>
													)}
												</div>
											</td>

											{/* Brand Column */}
											<td className="text-center">
												<span className="brand-name">{p.brand?.name || "-"}</span>
											</td>

											{/* Type Column */}
											<td className="text-center">
												<span className="product-type">{p.product_type?.title || "-"}</span>
											</td>

											{/* Flavor Column */}
											<td className="text-center">
												<span className="flavor-text">
													{p.flavor ? toCamelCase(p.flavor) : "-"}
												</span>
											</td>

											{/* Version Column */}
											<td className="text-center">
												<span className="version-badge">v{p.version}</span>
											</td>

											{/* Country Column */}
											<td className="text-center">
												<div className="country-flag">
													<i
														className={`${p.country?.alpha_2?.toLowerCase() || "us"} flag list-flag`}
													></i>
												</div>
											</td>

											{/* Status Column */}
											<td className="text-center">
												<span
													className={`status-badge status-${productViewType === "active" ? "retail" : "concept"}`}
												>
													{productViewType === "active" ? "Retail" : "Concept"}
												</span>
											</td>

											{/* Score Column */}
											<td className="text-center">
												<div className="score-container">
													<ProgressGradient color={"#7c4aa9"} value={50} height={8} />
													<span className="score-text">50%</span>
												</div>
											</td>

											{/* Actions Column */}
											<td className="text-center">
												<button
													className="action-button"
													onClick={(e) => {
														e.stopPropagation();
														props.history.push(`${routes.PRODUCTS}/${p.id}`);
													}}
												>
													<ArrowRight size={16} />
													<span>View</span>
												</button>
											</td>
										</tr>
									);
								})}
							</tbody>
						)}
					</Table>
				</div>

			)}

		</div>
	);
});

const mapStateToProps = (state: IApplicationState) => ({
	active: state.product.active,
	concept: state.product.concept,
	profile: state.user.profile,
	selected_brand: state.user.selected_brand,
	filters: state.product.filters
});

const mapDispatchToProps = {
	getActiveProducts: () => productService.getActiveProducts(),
	getConceptProducts: () => productService.getConceptProducts(),
	getAllProductsForFilters: () => productService.getAllProductsForFilters(),
	getAllConceptProductsForFilters: () => productService.getAllConceptProductsForFilters(),
	paginateConceptProducts: (page: number) => (dispatch: Dispatch) => {
		dispatch(paginateAction(product.PAGINATE_CONCEPT_PRODUCT, page));
	},
	paginateActiveProducts: (page: number) => (dispatch: Dispatch) => {
		dispatch(paginateAction(product.PAGINATE_ACTIVE_PRODUCT, page));
	},
	setProductFilters: (filters: {
		selectedMarket?: string;
		selectedBrand?: string;
		selectedType?: string;
		searchTerm?: string;
	}) => (dispatch: Dispatch) => {
		dispatch(setProductFiltersAction(filters));
	},
	clearProductFilters: () => (dispatch: Dispatch) => {
		dispatch(clearProductFiltersAction());
	}
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ProductList));


